export const restaurantLocales = ["fr", "en", "es"] as const;

export type RestaurantLocale = (typeof restaurantLocales)[number];

export interface LocalizedText {
  fr: string;
  en: string;
  es: string;
}

export interface NavigationItemConfig {
  key: string;
  href: string;
  labels: LocalizedText;
  isActive: boolean;
  showInNav: boolean;
  showInFooter: boolean;
}

export interface QuickInfoBlockConfig {
  key: "hours" | "address" | "contact" | "reservation";
  icon: "clock" | "map-pin" | "phone" | "calendar";
  title: LocalizedText;
  line1: LocalizedText;
  line2: LocalizedText;
  actionLabel: LocalizedText;
  actionHref: string;
  isActive: boolean;
}

export interface HighlightCardConfig {
  key: "event" | "weeklyDish" | "wine";
  category: LocalizedText;
  title: LocalizedText;
  body: LocalizedText;
  imageUrl: string;
  imageAlt: LocalizedText;
  isActive: boolean;
}

export interface HomeContentConfig {
  restaurantName: string;
  phone: string;
  email: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  postalCode: string;
  country: string;
  googleMapsUrl: string;
  hero: {
    eyebrow: LocalizedText;
    title: LocalizedText;
    subtitle: LocalizedText;
    menuCta: LocalizedText;
    reservationCta: LocalizedText;
    imageUrl: string;
    imageAlt: LocalizedText;
    showMenuCta: boolean;
    showReservationCta: boolean;
  };
  quickInfo: QuickInfoBlockConfig[];
  restaurantIntro: {
    eyebrow: LocalizedText;
    title: LocalizedText;
    body: LocalizedText;
    features: LocalizedText[];
    imageUrl: string;
    imageAlt: LocalizedText;
    secondaryImageUrl: string;
    secondaryImageAlt: LocalizedText;
    isActive: boolean;
  };
  highlights: {
    title: LocalizedText;
    subtitle: LocalizedText;
    allCta: LocalizedText;
    cards: HighlightCardConfig[];
    isActive: boolean;
  };
  hours: {
    title: LocalizedText;
    openBadge: LocalizedText;
    openDescription: LocalizedText;
    closedBadge: LocalizedText;
    closedDescription: LocalizedText;
    imageUrl: string;
    imageAlt: LocalizedText;
    isActive: boolean;
  };
  footerCtas: {
    menuEyebrow: LocalizedText;
    menuTitle: LocalizedText;
    menuHref: string;
    takeawayEyebrow: LocalizedText;
    takeawayTitle: LocalizedText;
    takeawayHref: string;
    showMenu: boolean;
    showTakeaway: boolean;
  };
  seo: {
    title: LocalizedText;
    description: LocalizedText;
    localKeywords: string[];
  };
  navigation: NavigationItemConfig[];
}

const cloudflareBase =
  process.env.NEXT_PUBLIC_CLOUDFLARE_R2_PUBLIC_URL ||
  "https://a440fa56fde24e201bd66ca3f3115b7c.r2.cloudflarestorage.com/lebbrasserie";

export const defaultNavigation: NavigationItemConfig[] = [
  {
    key: "home",
    href: "/",
    labels: { fr: "Accueil", en: "Home", es: "Inicio" },
    isActive: true,
    showInNav: true,
    showInFooter: true,
  },
  {
    key: "menu",
    href: "/menu",
    labels: { fr: "Menu", en: "Menu", es: "Carta" },
    isActive: true,
    showInNav: true,
    showInFooter: true,
  },
  {
    key: "whatsOn",
    href: "/en-ce-moment",
    labels: { fr: "En ce moment", en: "What's on", es: "Actualidad" },
    isActive: true,
    showInNav: true,
    showInFooter: true,
  },
  {
    key: "contact",
    href: "/contact",
    labels: { fr: "Contact", en: "Contact", es: "Contacto" },
    isActive: true,
    showInNav: true,
    showInFooter: true,
  },
  {
    key: "takeaway",
    href: "/commande-a-emporter",
    labels: { fr: "À emporter", en: "Takeaway", es: "Para llevar" },
    isActive: false,
    showInNav: false,
    showInFooter: false,
  },
];

export const defaultHomeContent: HomeContentConfig = {
  restaurantName: "Le B Brasserie",
  phone: "07 49 76 69 15",
  email: "contact@leb-brasserie.com",
  addressLine1: "31 Route de Sault de Navailles",
  addressLine2: "64230 Bougarber, France",
  city: "Bougarber",
  postalCode: "64230",
  country: "France",
  googleMapsUrl:
    "https://www.google.com/maps/search/?api=1&query=31%20Route%20de%20Sault%20de%20Navailles%2064230%20Bougarber",
  hero: {
    eyebrow: {
      fr: "Une adresse qui a du goût",
      en: "A place with real taste",
      es: "Una dirección con mucho sabor",
    },
    title: {
      fr: "Le coin qu'on cherchait sans le savoir.",
      en: "The spot we were looking for without knowing it.",
      es: "El rincón que buscábamos sin saberlo.",
    },
    subtitle: {
      fr: "Une carte sincère, une cuisine qui ne ment pas. Bienvenue au B.",
      en: "An honest menu, cooking that does not pretend. Welcome to Le B.",
      es: "Una carta honesta, una cocina sin artificios. Bienvenidos a Le B.",
    },
    menuCta: { fr: "Découvrir la carte", en: "View the menu", es: "Ver la carta" },
    reservationCta: {
      fr: "Contactez-nous",
      en: "Contact us",
      es: "Contacta con nosotros",
    },
    imageUrl:
      "https://images.unsplash.com/photo-1555396273-367ea4eb4db5?auto=format&fit=crop&w=1800&q=85",
    imageAlt: {
      fr: "Salle chaleureuse de brasserie avec tables dressées et lumière douce",
      en: "Warm brasserie dining room with set tables and soft light",
      es: "Sala cálida de brasserie con mesas preparadas y luz suave",
    },
    showMenuCta: true,
    showReservationCta: true,
  },
  quickInfo: [
    {
      key: "hours",
      icon: "clock",
      title: { fr: "Horaires", en: "Hours", es: "Horario" },
      line1: { fr: "Ouvert aujourd'hui", en: "Open today", es: "Abierto hoy" },
      line2: { fr: "11h45-14h30", en: "11:45am-2:30pm", es: "11:45-14:30" },
      actionLabel: {
        fr: "Voir tous les horaires",
        en: "See all hours",
        es: "Ver todos los horarios",
      },
      actionHref: "#horaires",
      isActive: true,
    },
    {
      key: "address",
      icon: "map-pin",
      title: { fr: "Adresse", en: "Address", es: "Dirección" },
      line1: {
        fr: "31 Route de Sault de Navailles,",
        en: "31 Route de Sault de Navailles,",
        es: "31 Route de Sault de Navailles,",
      },
      line2: {
        fr: "64230 Bougarber, France",
        en: "64230 Bougarber, France",
        es: "64230 Bougarber, Francia",
      },
      actionLabel: { fr: "Itinéraire maps", en: "Maps directions", es: "Cómo llegar" },
      actionHref:
        "https://www.google.com/maps/search/?api=1&query=31%20Route%20de%20Sault%20de%20Navailles%2064230%20Bougarber",
      isActive: true,
    },
    {
      key: "contact",
      icon: "phone",
      title: { fr: "Contact", en: "Contact", es: "Contacto" },
      line1: { fr: "07 49 76 69 15", en: "07 49 76 69 15", es: "07 49 76 69 15" },
      line2: { fr: "Écrire ou appeler", en: "Write or call", es: "Escribir o llamar" },
      actionLabel: { fr: "Écrire", en: "Write", es: "Escribir" },
      actionHref: "mailto:contact@leb-brasserie.com",
      isActive: true,
    },
    {
      key: "reservation",
      icon: "calendar",
      title: {
        fr: "Réserver une table",
        en: "Book a table",
        es: "Reservar mesa",
      },
      line1: { fr: "Je veux ma table", en: "I want my table", es: "Quiero mi mesa" },
      line2: {
        fr: "Un appel et on s'organise",
        en: "Call us and we will sort it",
        es: "Llámanos y lo organizamos",
      },
      actionLabel: { fr: "Contactez-nous", en: "Contact us", es: "Contacta con nosotros" },
      actionHref: "tel:0749766915",
      isActive: true,
    },
  ],
  restaurantIntro: {
    eyebrow: { fr: "Le restaurant", en: "The restaurant", es: "El restaurante" },
    title: {
      fr: "Un endroit où l'on revient",
      en: "A place you come back to",
      es: "Un lugar al que siempre se vuelve",
    },
    body: {
      fr:
        "Le B, c'est une brasserie qui assume ce qu'elle est : généreuse, sans prétention, et diablement bonne. Une carte qui mélange les influences sans jamais perdre le fil — maison, sincère, appétissante. Et dans l'assiette, la générosité d'un chef qui a ramené l'Italie dans sa valise.",
      en:
        "Le B is a brasserie that knows exactly what it is: generous, unfussy and seriously good. A menu that mixes influences without losing the thread: homemade, honest, appetising. On the plate, the generosity of a chef who brought Italy back in his suitcase.",
      es:
        "Le B es una brasserie que sabe lo que es: generosa, sin pretensiones y realmente buena. Una carta que mezcla influencias sin perder el hilo: casera, sincera, apetecible. En el plato, la generosidad de un chef que trajo Italia en la maleta.",
    },
    features: [
      {
        fr: "Grande terrasse et espace pour vos événements privés",
        en: "Large terrace and space for private events",
        es: "Gran terraza y espacio para eventos privados",
      },
      {
        fr: "Ingrédients sélectionnés, portions généreuses",
        en: "Selected ingredients, generous portions",
        es: "Ingredientes seleccionados y raciones generosas",
      },
      {
        fr: "Une adresse où toute la famille trouve son bonheur",
        en: "A place where the whole family finds something to love",
        es: "Un lugar donde toda la familia encuentra algo que le gusta",
      },
      {
        fr: "Une cuisine de brasserie française avec une âme italienne",
        en: "French brasserie cooking with an Italian soul",
        es: "Cocina de brasserie francesa con alma italiana",
      },
    ],
    imageUrl: "/uploads/restaurant/le-b-burrata-terrasse.jpeg",
    imageAlt: {
      fr: "Burrata servie avec tomates cerises et frites en terrasse au B Brasserie",
      en: "Burrata served with cherry tomatoes and fries on the terrace at Le B Brasserie",
      es: "Burrata servida con tomates cherry y patatas fritas en la terraza de Le B Brasserie",
    },
    secondaryImageUrl: "/uploads/restaurant/le-b-comptoir.jpeg",
    secondaryImageAlt: {
      fr: "Comptoir en bois et salle chaleureuse du B Brasserie",
      en: "Wooden counter and warm dining room at Le B Brasserie",
      es: "Barra de madera y sala acogedora de Le B Brasserie",
    },
    isActive: true,
  },
  highlights: {
    title: { fr: "En ce moment", en: "What's on", es: "Actualidad" },
    subtitle: {
      fr: "Les temps forts de la brasserie",
      en: "Current highlights at the brasserie",
      es: "Los momentos destacados de la brasserie",
    },
    allCta: { fr: "Voir toutes les actualités", en: "See all news", es: "Ver toda la actualidad" },
    isActive: true,
    cards: [
      {
        key: "event",
        category: { fr: "Événement", en: "Event", es: "Evento" },
        title: { fr: "Soirées du vendredi", en: "Friday nights", es: "Noches de viernes" },
        body: {
          fr:
            "Chaque vendredi soir, on rallonge les horaires et on monte le volume. Bonne bouffe, bons verres, bonne humeur — réservez votre table.",
          en:
            "Every Friday evening, we extend the hours and turn up the mood. Good food, good glasses, good energy. Book your table.",
          es:
            "Cada viernes por la noche ampliamos el horario y subimos el ambiente. Buena comida, buenas copas y buen humor. Reserva tu mesa.",
        },
        imageUrl:
          "https://images.unsplash.com/photo-1514933651103-005eec06c04b?auto=format&fit=crop&w=1000&q=85",
        imageAlt: {
          fr: "Ambiance de bar de brasserie le soir",
          en: "Evening brasserie bar atmosphere",
          es: "Ambiente de bar de brasserie por la noche",
        },
        isActive: true,
      },
      {
        key: "weeklyDish",
        category: { fr: "Cuisine", en: "Kitchen", es: "Cocina" },
        title: { fr: "Le plat de la semaine", en: "Dish of the week", es: "Plato de la semana" },
        body: {
          fr:
            "Notre chef vous prépare chaque semaine une formule surprise en ardoise, à prix doux.",
          en: "Every week, our chef prepares a surprise blackboard special at a fair price.",
          es: "Cada semana, nuestro chef prepara una fórmula sorpresa en pizarra a buen precio.",
        },
        imageUrl:
          "https://images.unsplash.com/photo-1504674900247-0877df9cc836?auto=format&fit=crop&w=1000&q=85",
        imageAlt: {
          fr: "Plat de saison dressé en cuisine",
          en: "Seasonal dish plated in the kitchen",
          es: "Plato de temporada emplatado en cocina",
        },
        isActive: true,
      },
      {
        key: "wine",
        category: { fr: "Sélection", en: "Selection", es: "Selección" },
        title: { fr: "Notre sélection de vins", en: "Our wine selection", es: "Nuestra selección de vinos" },
        body: {
          fr:
            "Quelques bouteilles bien choisies pour accompagner votre repas. Des producteurs que l'on aime, des étiquettes qui racontent quelque chose.",
          en:
            "A few carefully chosen bottles to pair with your meal. Producers we like, labels with something to say.",
          es:
            "Algunas botellas bien elegidas para acompañar la comida. Productores que nos gustan, etiquetas que cuentan algo.",
        },
        imageUrl:
          "https://images.unsplash.com/photo-1510812431401-41d2bd2722f3?auto=format&fit=crop&w=1000&q=85",
        imageAlt: {
          fr: "Bouteilles de vin alignées sur une étagère",
          en: "Wine bottles lined up on a shelf",
          es: "Botellas de vino alineadas en una estantería",
        },
        isActive: true,
      },
    ],
  },
  hours: {
    title: { fr: "Horaires", en: "Hours", es: "Horario" },
    openBadge: {
      fr: "Ouvert maintenant — on vous attend !",
      en: "Open now — we are waiting for you!",
      es: "Abierto ahora — te esperamos!",
    },
    openDescription: {
      fr: "Les cuisines sont ouvertes jusqu'à 14h30",
      en: "The kitchen is open until 2:30pm",
      es: "La cocina está abierta hasta las 14:30",
    },
    closedBadge: {
      fr: "Fermé mais pas pour longtemps !",
      en: "Closed, but not for long!",
      es: "Cerrado, pero no por mucho tiempo!",
    },
    closedDescription: {
      fr: "Nous ouvrons à 11h45",
      en: "We open at 11:45am",
      es: "Abrimos a las 11:45",
    },
    imageUrl:
      "https://images.unsplash.com/photo-1414235077428-338989a2e8c0?auto=format&fit=crop&w=1200&q=85",
    imageAlt: {
      fr: "Salle de restaurant prête pour le service",
      en: "Restaurant room ready for service",
      es: "Sala del restaurante lista para el servicio",
    },
    isActive: true,
  },
  footerCtas: {
    menuEyebrow: { fr: "Menu", en: "Menu", es: "Carta" },
    menuTitle: { fr: "Découvrir la carte", en: "View the menu", es: "Ver la carta" },
    menuHref: "/menu",
    takeawayEyebrow: { fr: "À emporter", en: "Takeaway", es: "Para llevar" },
    takeawayTitle: {
      fr: "À emporter, c'est par ici",
      en: "Takeaway starts here",
      es: "Para llevar, por aquí",
    },
    takeawayHref: "/commande-a-emporter",
    showMenu: true,
    showTakeaway: false,
  },
  seo: {
    title: {
      fr: "Le B Brasserie | Restaurant convivial à Bougarber proche de Pau",
      en: "Le B Brasserie | Friendly restaurant in Bougarber near Pau",
      es: "Le B Brasserie | Restaurante acogedor en Bougarber cerca de Pau",
    },
    description: {
      fr:
        "Brasserie conviviale à Bougarber, proche de Pau. Burgers, viandes, planches, belle carte végétarienne, touche italienne. Terrasse et bonne humeur garantie.",
      en:
        "Friendly brasserie in Bougarber near Pau. Burgers, meats, sharing boards, vegetarian dishes, Italian touch, terrace and good mood.",
      es:
        "Brasserie acogedora en Bougarber, cerca de Pau. Burgers, carnes, tablas, carta vegetariana, toque italiano, terraza y buen ambiente.",
    },
    localKeywords: ["Bougarber", "Pau", "Orthez", "Béarn"],
  },
  navigation: defaultNavigation,
};

export function localized(value: LocalizedText, locale: RestaurantLocale): string {
  return value[locale] || value.fr;
}

export function cloudflareAsset(path: string): string {
  return `${cloudflareBase}/${path.replace(/^\/+/, "")}`;
}
